import { useEffect } from 'react';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface GoogleAnalyticsProps {
  measurementId: string;
  enabled?: boolean;
}

export const GoogleAnalytics = ({ measurementId, enabled = true }: GoogleAnalyticsProps) => {
  useEffect(() => {
    if (!measurementId) return;

    // Initialize dataLayer and gtag function immediately (queues commands)
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };

    // Set default consent state via Consent Mode v2
    window.gtag('consent', 'default', {
      analytics_storage: 'granted',
    });

    if (!enabled) {
      // If not enabled (user declined), update consent to denied
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
      return;
    }

    // Check if script already loaded
    if (document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`)) {
      return;
    }

    // Load GA script FIRST
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => {
      // Fire config AFTER script is ready
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
    };
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector(`script[src*="${measurementId}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [measurementId, enabled]);

  return null;
};

// Helper function to track events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Helper function to track page views
export const trackPageView = (measurementId: string, pagePath: string, pageTitle?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const title = pageTitle || document.title || 'Sink Bike Run';
    
    window.gtag('config', measurementId, {
      page_path: pagePath,
      page_title: title,
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  }
};
